import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  SvgIcon,
  Tooltip,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import { useNavigate, useOutletContext } from 'react-router-dom';
import NoAccess from '../components/NoAccess';
import BooleanFloatingFilter from '../components/BooleanFloatingFilter';
import { gridTheme, defaultColDef, centeredCellStyle, centerAlignedCellStyle, rightAlignedCellStyle } from '../services/aggrid';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Panel, Chip, Btn, Dot, Portrait, PageHead, Tile, I } from '../components/hud';

axios.defaults.withCredentials = true;

const API_BASE_URL = (import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:5000')) + '/api';

// Discord brand mark (kept: no HUD equivalent)
const DiscordIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </SvgIcon>
);

const formatISK = (amount) => {
  if (amount >= 1000000000) return (amount / 1000000000).toFixed(2) + 'B ISK';
  if (amount >= 1000000) return (amount / 1000000).toFixed(2) + 'M ISK';
  if (amount >= 1000) return (amount / 1000).toFixed(2) + 'K ISK';
  return amount.toLocaleString() + ' ISK';
};

const formatLastOnline = (date) => {
  if (!date) return 'Never';
  const diffMs = new Date() - new Date(date);
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) return `${Math.floor(diffMs / (1000 * 60))}m ago`;
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  const months = Math.floor(diffDays / 30);
  return `${months} month${months > 1 ? 's' : ''} ago`;
};

const getOnlineStatusTone = (date) => {
  if (!date) return 'danger';
  const diffDays = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return 'ok';
  if (diffDays <= 7) return 'warn';
  return 'danger';
};

// Cell renderers
const CharacterCell = ({ data }) => {
  const inactive = data.isActive === false;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: '100%' }}>
      <Portrait
        name={data.characterName}
        size={36}
        src={`https://images.evetech.net/characters/${data.characterId}/portrait?size=64`}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span className="cell-strong" style={{ opacity: inactive ? 0.6 : 1 }}>
          {data.characterName}
        </span>
        {data.numAccounts > 1 && (
          <Chip tone="ghost">{data.numAccounts} accts</Chip>
        )}
        {inactive && <Chip tone="danger">Inactive</Chip>}
      </div>
    </div>
  );
};

const EventsCell = ({ value }) => (
  <Chip tone={value > 0 ? '' : 'ghost'}>{value}</Chip>
);

const FeesCell = ({ value }) => (
  <span
    className="mono"
    style={{
      color: value > 0 ? 'var(--danger)' : 'var(--text-3)',
      fontWeight: value > 0 ? 600 : 400
    }}
  >
    {value > 0 ? formatISK(value) : '—'}
  </span>
);

const LastLoginCell = ({ data }) => {
  const tone = getOnlineStatusTone(data.lastLogin);
  const color = tone === 'ok' ? 'var(--ok)' : tone === 'warn' ? 'var(--amber)' : 'var(--danger)';
  return (
    <span className="mono" style={{ color }}>
      {formatLastOnline(data.lastLogin)}
    </span>
  );
};

const RegisteredCell = ({ value }) => (
  <Tooltip title={value === 'Yes' ? 'Has logged into the app' : 'Never logged in (main or any alt)'}>
    <span>
      {value === 'Yes'
        ? <Dot tone="ok" />
        : <Dot tone="danger" />}
    </span>
  </Tooltip>
);

const DiscordCell = ({ value, data }) => (
  <Tooltip
    title={
      value === 'Yes'
        ? (data.discordUsername ? `Linked: ${data.discordUsername}` : 'Discord linked')
        : 'No Discord linked'
    }
  >
    <span>
      <DiscordIcon
        fontSize="small"
        sx={{ color: value === 'Yes' ? 'var(--ok)' : 'var(--text-3)' }}
      />
    </span>
  </Tooltip>
);

function MemberStats() {
  const navigate = useNavigate();
  const { user } = useOutletContext();

  if (!user?.permissions?.canViewMembers) {
    return <NoAccess feature="Member Stats" requiredRole="Director, CEO, or HR role" />;
  }

  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideInactive, setHideInactive] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [registeredFilter, setRegisteredFilter] = useState('any');
  const [discordFilter, setDiscordFilter] = useState('any');

  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [commentsModalMember, setCommentsModalMember] = useState(null);
  const [commentsValue, setCommentsValue] = useState('');
  const [savingComments, setSavingComments] = useState(false);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/members/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching member stats:', err);
      if (err.response?.status === 401) {
        navigate('/?error=session_expired');
        return;
      }
      setError(err.response?.data?.error || 'Failed to fetch member stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const totals = useMemo(() => stats.reduce(
    (acc, m) => ({
      eventsAttended: acc.eventsAttended + m.eventsAttended,
      feesOpen: acc.feesOpen + m.feesOpen
    }),
    { eventsAttended: 0, feesOpen: 0 }
  ), [stats]);

  const activeCount = useMemo(() => stats.filter(m => m.isActive !== false).length, [stats]);
  const inactiveCount = useMemo(() => stats.length - activeCount, [stats, activeCount]);

  const rowData = useMemo(() => {
    let rows = hideInactive ? stats.filter(m => m.isActive !== false) : stats;
    if (registeredFilter !== 'any') {
      const want = registeredFilter === 'yes';
      rows = rows.filter(m => !!m.registered === want);
    }
    if (discordFilter !== 'any') {
      const want = discordFilter === 'yes';
      rows = rows.filter(m => !!m.discordLinked === want);
    }
    return rows;
  }, [stats, hideInactive, registeredFilter, discordFilter]);

  // Build histogram bands from real eventsAttended values
  const histogramBands = useMemo(() => {
    if (!stats.length) return [];
    const bands = [
      { label: '0', min: 0, max: 0 },
      { label: '1–5', min: 1, max: 5 },
      { label: '6–10', min: 6, max: 10 },
      { label: '11–20', min: 11, max: 20 },
      { label: '21–50', min: 21, max: 50 },
      { label: '50+', min: 51, max: Infinity },
    ];
    const counts = bands.map(b => ({
      label: b.label,
      count: stats.filter(m => m.eventsAttended >= b.min && m.eventsAttended <= b.max).length,
    }));
    const maxCount = Math.max(...counts.map(b => b.count), 1);
    return counts.map(b => ({ ...b, pct: (b.count / maxCount) * 100 }));
  }, [stats]);

  const handleOpenComments = useCallback((member) => {
    setCommentsModalMember(member);
    setCommentsValue(member.comments || '');
    setCommentsModalOpen(true);
  }, []);

  const handleCloseComments = () => {
    setCommentsModalOpen(false);
    setCommentsModalMember(null);
    setCommentsValue('');
  };

  const handleSaveComments = async () => {
    if (!commentsModalMember) return;
    try {
      setSavingComments(true);
      await axios.patch(`${API_BASE_URL}/members/${commentsModalMember.characterId}/comments`, {
        comments: commentsValue
      });
      setStats(stats.map(s =>
        s.characterId === commentsModalMember.characterId
          ? { ...s, comments: commentsValue }
          : s
      ));
      handleCloseComments();
    } catch (err) {
      console.error('Error saving comments:', err);
      setError(err.response?.data?.error || 'Failed to save comments');
    } finally {
      setSavingComments(false);
    }
  };

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  }), []);

  const CommentsCell = useCallback((params) => (
    <Tooltip title={params.data.comments ? 'View/Edit Comments' : 'Add Comments'}>
      <span>
        <Btn
          tone={params.data.comments ? '' : 'ghost'}
          onClick={() => handleOpenComments(params.data)}
          style={{ padding: '4px 10px', fontSize: 10 }}
        >
          {params.data.comments ? 'NOTE' : '+ NOTE'}
        </Btn>
      </span>
    </Tooltip>
  ), [handleOpenComments]);

  const columnDefs = useMemo(() => [
    {
      field: 'characterName',
      headerName: 'Character',
      flex: 2,
      minWidth: 260,
      filter: 'agTextColumnFilter',
      cellRenderer: CharacterCell
    },
    {
      field: 'eventsAttended',
      headerName: 'Events',
      width: 130,
      filter: 'agNumberColumnFilter',
      headerClass: 'ag-right-aligned-header',
      cellStyle: centerAlignedCellStyle,
      cellRenderer: EventsCell
    },
    {
      field: 'feesOpen',
      headerName: 'Fees Open',
      width: 160,
      filter: 'agNumberColumnFilter',
      cellStyle: rightAlignedCellStyle,
      cellRenderer: FeesCell
    },
    {
      field: 'lastLogin',
      headerName: 'Last Login',
      width: 160,
      valueGetter: (params) => params.data.lastLogin ? new Date(params.data.lastLogin) : null,
      filter: 'agDateColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: LastLoginCell
    },
    {
      headerName: 'Registered',
      width: 130,
      valueGetter: (params) => params.data.registered ? 'Yes' : 'No',
      filter: false,
      floatingFilter: false,
      cellStyle: centerAlignedCellStyle,
      cellRenderer: RegisteredCell
    },
    {
      headerName: 'Discord',
      width: 120,
      valueGetter: (params) => params.data.discordLinked ? 'Yes' : 'No',
      filter: false,
      floatingFilter: false,
      cellStyle: centerAlignedCellStyle,
      cellRenderer: DiscordCell
    },
    {
      headerName: 'Notes',
      width: 120,
      filter: false,
      sortable: false,
      cellStyle: centerAlignedCellStyle,
      cellRenderer: CommentsCell
    }
  ], [CommentsCell]);

  const getRowStyle = useCallback((params) => (
    params.data?.isActive === false
      ? { backgroundColor: 'rgba(255,255,255,0.04)', opacity: 0.7 }
      : undefined
  ), []);

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Dot tone="pulse" />
          <span className="label" style={{ marginLeft: 12 }}>Loading telemetry...</span>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead
        crumb="Personnel // Analytics"
        id={`${stats.length} Pilots`}
        title="Member Statistics"
        actions={
          <>
            <Btn icon={I.Sync} onClick={fetchStats}>Refresh</Btn>
          </>
        }
      />

      {error && (
        <div style={{
          padding: '12px 14px',
          background: 'rgba(255,91,106,0.08)',
          borderLeft: '2px solid var(--danger)',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--text-1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12
        }}>
          <span><span style={{ color: 'var(--danger)', letterSpacing: '0.18em', textTransform: 'uppercase', marginRight: 10 }}>Error</span>{error}</span>
          <Btn tone="danger" onClick={() => setError(null)} style={{ padding: '4px 10px', fontSize: 10 }}>Dismiss</Btn>
        </div>
      )}

      {/* Telemetry strip — 4 tiles from real data */}
      <div className="telemetry-strip">
        <Tile k="Total Members" tone="">
          <div className="v">{stats.length}<span className="unit">Pilots</span></div>
        </Tile>
        <Tile k="Active" tone="ok">
          <div className="v">{activeCount}<span className="unit">Online recently</span></div>
        </Tile>
        <Tile k="Inactive" tone="amber">
          <div className="v">{inactiveCount}<span className="unit">Marked inactive</span></div>
        </Tile>
        <Tile k="Fees Outstanding" tone={totals.feesOpen > 0 ? 'magenta' : ''}>
          <div className="v" style={{ fontSize: totals.feesOpen > 0 ? 20 : 26 }}>
            {totals.feesOpen > 0 ? formatISK(totals.feesOpen) : '—'}
          </div>
        </Tile>
      </div>

      {/* Events distribution histogram — built from real eventsAttended values */}
      {histogramBands.length > 0 && (
        <Panel title="Events Attended" sub="Distribution">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {histogramBands.map((band) => (
              <div key={band.label} style={{ display: 'grid', gridTemplateColumns: '52px 1fr 42px', alignItems: 'center', gap: 10 }}>
                <span className="label">{band.label}</span>
                <div className="fuel-bar" style={{ height: 8 }}>
                  <span
                    className="fill"
                    style={{
                      width: `${band.pct}%`,
                      background: 'var(--accent)',
                      boxShadow: '0 0 8px var(--accent-glow)'
                    }}
                  />
                </div>
                <span className="mono" style={{ color: 'var(--text-2)', fontSize: 11, textAlign: 'right' }}>
                  {band.count}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Pilot statistics ag-grid */}
      <Panel
        title="Pilot Statistics"
        sub={`${rowData.length} / ${stats.length} Rows`}
        action={
          <div className="filter-bar" style={{ marginBottom: 0 }}>
            <div className="hud-input search">
              {I.Search}
              <input
                placeholder="Quick search..."
                value={quickFilter}
                onChange={(e) => setQuickFilter(e.target.value)}
              />
            </div>
            <select
              className="hud-select"
              value={registeredFilter}
              onChange={(e) => setRegisteredFilter(e.target.value)}
            >
              <option value="any">ALL REGISTERED</option>
              <option value="yes">REGISTERED</option>
              <option value="no">NOT REGISTERED</option>
            </select>
            <select
              className="hud-select"
              value={discordFilter}
              onChange={(e) => setDiscordFilter(e.target.value)}
            >
              <option value="any">ALL DISCORD</option>
              <option value="yes">DISCORD LINKED</option>
              <option value="no">NO DISCORD</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <div
                className={`toggle ${hideInactive ? 'on' : ''}`}
                onClick={() => setHideInactive(v => !v)}
                role="switch"
                aria-checked={hideInactive}
              />
              <span className="label">Hide inactive</span>
            </label>
          </div>
        }
      >
        <div className="hud-grid">
          <AgGridReact
            theme={gridTheme}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            domLayout="autoHeight"
            rowHeight={64}
            headerHeight={40}
            floatingFiltersHeight={36}
            pagination
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 100]}
            quickFilterText={quickFilter}
            getRowStyle={getRowStyle}
            getRowId={(params) => params.data.characterId}
            animateRows
          />
        </div>
      </Panel>

      {/* Comments Modal — kept as MUI Dialog (rich-text editor dependency) */}
      <Dialog
        open={commentsModalOpen}
        onClose={handleCloseComments}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--bg-2)',
            border: '1px solid var(--line-strong)',
            borderRadius: 0,
            color: 'var(--text-0)',
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'var(--font-display)',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          fontSize: 14,
          borderBottom: '1px solid var(--line)',
          color: 'var(--text-0)',
        }}>
          Notes — {commentsModalMember?.characterName}
        </DialogTitle>
        <DialogContent>
          <div style={{
            marginTop: 8,
          }}>
            <style>{`
              .ql-toolbar { border-color: var(--line-strong) !important; border-radius: 2px 2px 0 0; }
              .ql-toolbar .ql-stroke { stroke: var(--text-1) !important; }
              .ql-toolbar .ql-fill { fill: var(--text-1) !important; }
              .ql-toolbar .ql-picker-label { color: var(--text-1) !important; }
              .ql-toolbar .ql-picker-options { background: var(--bg-2) !important; border-color: var(--line-strong) !important; }
              .ql-container { border-color: var(--line-strong) !important; border-radius: 0 0 2px 2px; min-height: 200px; font-size: 0.95rem; color: var(--text-0); background: var(--bg-1); }
              .ql-editor { min-height: 200px; color: var(--text-0); }
              .ql-editor.ql-blank::before { color: var(--text-3); font-style: italic; }
            `}</style>
            <ReactQuill
              theme="snow"
              value={commentsValue}
              onChange={setCommentsValue}
              modules={quillModules}
              placeholder="Add notes about this pilot..."
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid var(--line)', padding: '12px 16px', gap: 1 }}>
          <Btn onClick={handleCloseComments}>Cancel</Btn>
          <Btn tone="primary" onClick={handleSaveComments} disabled={savingComments}>
            {savingComments ? 'Saving...' : 'Save Notes'}
          </Btn>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MemberStats;
